/**
 * batch-45 onboarding (FO-381) — de flow-uitgangen: annuleren-met-opruiming,
 * verwijderen en geforceerd afronden ("Afronden buiten de flow om").
 *
 * Eén gedeeld request-schema per actie (CLAUDE.md *Validatie & foutmeldingen*):
 * de API parset ermee én het backoffice-form valideert er client-side mee, zodat
 * het form niets kan sturen dat de API weigert.
 *
 * Opruim-semantiek (spec 84 §3.1) — de dialoog toont wat er gebeurt; alleen het
 * medewerker-record is een echte keuze:
 *  - medewerker-record: verwijderen (soft-delete via het bestaande
 *    employee-delete-pad; personeelsnummer komt vrij, account gedeactiveerd,
 *    welkom-token ingetrokken) of behouden (kandidaat start mogelijk later).
 *  - gegenereerde documenten: altijd gearchiveerd (nooit hard weg).
 *  - openstaande taken: altijd gesloten; uitgegeven middelen leveren een
 *    inname-taak op (bestaand UITDIENST_INNAME-mechanisme).
 */

import { z } from 'zod'

// ─── Annuleren met opruiming ─────────────────────────────────────────────────

export const annuleerOnboardingSchema = z.object({
  /** Verplichte reden voor het annuleren (audit). */
  toelichting: z.string().trim().min(1, 'Toelichting is verplicht').max(2000),
  /**
   * Medewerker-record mee opruimen? Soft-delete via het bestaande
   * employee-delete-pad (vereist MEDEWERKERS.canDelete bovenop
   * ONBOARDING.canEdit). Default false: behouden is de veilige keuze; de UI
   * stelt verwijderen voor bij een CONCEPT-medewerker zonder verdere data.
   */
  medewerkerVerwijderen: z.boolean().default(false),
})
export type AnnuleerOnboardingInput = z.infer<typeof annuleerOnboardingSchema>

// ─── Onboarding verwijderen (ADMIN, vergissingen/testregels) ─────────────────

/**
 * DELETE `/onboarding/:id` — soft-delete van de onboarding-registratie zelf.
 * Alleen toegestaan zonder ondertekende documenten en zonder geboekte uren
 * (anders 409 met code `VERWIJDEREN_GEBLOKKEERD` -> annuleren-met-opruiming is
 * de route). Impliceert dezelfde opruiming als annuleren.
 */
export const verwijderOnboardingSchema = z.object({
  toelichting: z.string().trim().min(1, 'Toelichting is verplicht').max(2000),
  medewerkerVerwijderen: z.boolean().default(false),
})
export type VerwijderOnboardingInput = z.infer<typeof verwijderOnboardingSchema>

// ─── Geforceerd afronden ("Afronden buiten de flow om") ──────────────────────

/**
 * POST `/onboarding/:id/voltooi` — uitgebreid met `overslaanRest` (spec 84
 * §3.2). Zonder `overslaanRest` blijft de route exact de legacy-afronding
 * (alle stappen klaar vereist, IN_BEHANDELING -> AFGEROND). Mét `overslaanRest`
 * worden de resterende stappen als OVERGESLAGEN gemarkeerd (met één reden) en
 * springt de onboarding naar GEREED_VOOR_START: de spullen-toewijzing en het
 * bestaande, bewust handmatige activeer-moment blijven de slotstappen. Geen
 * tweede afrond-waarheid: de promotie loopt via de bestaande
 * GEREED_VOOR_START -> ACTIEF-overgang.
 *
 * FO-414 — `directActiveren` (alleen samen met `overslaanRest`): HR kiest in
 * dezelfde bevestiging óók het activeer-moment, zodat een onboarding met alle
 * stappen klaar in één actie naar ACTIEF kan. De activatie loopt server-side
 * via exact dezelfde activatie-bron als de "Activeren"-knop (zelfde promotie,
 * audits en nazorg); het blijft een expliciete HR-keuze, geen automatisme.
 */
export const voltooiOnboardingSchema = z
  .object({
    overslaanRest: z.boolean().default(false),
    /** Verplichte reden wanneer `overslaanRest` (komt als notitie op elke overgeslagen stap). */
    reden: z.string().trim().max(2000).optional(),
    /** FO-414 — na het klaarzetten meteen activeren (GEREED_VOOR_START -> ACTIEF). */
    directActiveren: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.overslaanRest && (!data.reden || data.reden.length === 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['reden'],
        message: 'Reden is verplicht bij afronden buiten de flow om',
      })
    }
    // FO-414 — direct activeren is een vervolg op het geforceerde afronden;
    // los ervan is het een stille no-op op de legacy-afronding. Weiger dat.
    if (data.directActiveren && !data.overslaanRest) {
      ctx.addIssue({
        code: 'custom',
        path: ['directActiveren'],
        message: 'Direct activeren kan alleen samen met afronden buiten de flow om',
      })
    }
  })
export type VoltooiOnboardingInput = z.infer<typeof voltooiOnboardingSchema>

// ─── Kopij (gedeeld tussen API-meldingen en UI) ──────────────────────────────

export const GEFORCEERD_AFRONDEN_LABEL = 'Afronden buiten de flow om'
